import React, { useEffect, useMemo, useState } from "react";
import {
  getNotificationsByUser,
  deleteNotificationById,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from "../../../services/notificationServices";
import { useNotification } from "../../../hooks/NotificationContext";
import NotificationCard from "./NotificationCard";
import { FaBell, FaCheckDouble, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import { useUser } from "../../Home/UserContext";
import ButtonEyeNoRead from "../../../components/Button/ButtonEyeNoRead";
import ButtonEyeRead from "../../../components/Button/ButtonEyeRead";
import ButtonDelete from "../../../components/Button/ButtonDelete";

export default function NotificationsDisplay({ userId, onCounts }) {
  const { user } = useUser();
  const [notifications, setNotifications] = useState([]);
  const { setUnreadCount } = useNotification();

  // Chargement
  useEffect(() => {
    const load = async () => {
      try {
        const data = await getNotificationsByUser(userId ?? user?.userId);
        setNotifications(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Erreur chargement notifications:", error);
        toast.error("Erreur lors du chargement des notifications.");
      }
    };
    load();
  }, [userId, user]);

  // Totaux
  const { total, unread } = useMemo(() => {
    const t = notifications.length;
    const u = notifications.reduce(
      (acc, n) => acc + (n.read_notification ? 0 : 1),
      0
    );
    return { total: t, unread: u };
  }, [notifications]);

  // ➜ MAJ badges (ne dépend QUE des valeurs)
  useEffect(() => {
    setUnreadCount(unread);
    if (onCounts) onCounts({ total, unread });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unread, total]); // ⬅️ on NE met PAS onCounts ici

  // Actions
  const toggleReadStatus = async (id) => {
    try {
      await markNotificationAsRead(id);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id ? { ...n, read_notification: !n.read_notification } : n
        )
      );
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteNotification = async (id) => {
    try {
      await deleteNotificationById(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      toast.success("Notification supprimée.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la suppression.");
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead(userId ?? user?.userId);
      setNotifications((prev) => prev.map((n) => ({ ...n, read_notification: true })));
      toast.success("Toutes les notifications sont marquées comme lues.");
    } catch (e) {
      console.error(e);
      toast.error("Impossible de tout marquer comme lu.");
    }
  };

  const handleDeleteAll = async () => {
    if (!notifications.length) {
      toast.info("Aucune notification à supprimer.");
      return;
    }
    if (!window.confirm("Supprimer toutes les notifications ?")) return;
    try {
      await Promise.all(notifications.map((n) => deleteNotificationById(n.id)));
      setNotifications([]);
      toast.success("Toutes les notifications ont été supprimées.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de la suppression.");
    }
  };

  return (
    <div className="space-y-4">
      {/* Titre + boutons compacts */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
          <div className="relative">
            <FaBell className="text-xl" />
            {unread > 0 && (
              <span className="absolute -top-2 -right-4 bg-violet-600 text-white text-[10px] font-bold rounded-full px-1.5 py-[1px]">
                {unread}
              </span>
            )}
          </div>
          <span className="text-sl font-bold ml-4">Notifications</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleMarkAllAsRead}
            className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs ring-1 ring-slate-300 dark:ring-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            title="Tout marquer comme lu"
          >
            <FaCheckDouble className="text-[12px]" />
            <span className="hidden sm:inline">Tout marquer lu</span>
          </button>
          <button
            type="button"
            onClick={handleDeleteAll}
            className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs ring-1 ring-slate-300 dark:ring-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            title="Tout supprimer"
          >
            <FaTrash className="text-[12px]" />
            <span className="hidden sm:inline">Tout supprimer</span>
          </button>
        </div>
      </div>

      {notifications.length > 0 ? (
        <>
          {/* Desktop */}
          <div className="hidden sm:block">
            <table className="min-w-full bg-white dark:bg-slate-900 divide-y divide-slate-200 dark:divide-slate-700 rounded-xl overflow-hidden text-[13px]">
              <thead className="bg-slate-50 dark:bg-slate-800">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 text-[11px]">Date</th>
                  <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 text-[11px]">Notification</th>
                  <th className="px-3 py-2 text-left font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 text-[11px]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {notifications.map((n) => {
                  const d = new Date(n.created_at);
                  const dateStr = d.toLocaleDateString("fr-FR", { year: "2-digit", month: "2-digit", day: "2-digit" });
                  const timeStr = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
                  return (
                    <tr key={n.id} className={`${n.read_notification ? "bg-slate-50 dark:bg-slate-900/40" : "bg-white dark:bg-slate-900"} hover:bg-slate-100 dark:hover:bg-slate-800`}>
                      <td className="px-3 py-2">
                        <div className="text-xs font-medium text-slate-800 dark:text-slate-200">{dateStr}</div>
                        <div className="text-[10px] text-slate-500 dark:text-slate-400">{timeStr}</div>
                      </td>
                      <td className="px-3 py-2 text-slate-700 dark:text-slate-300 text-[13px] leading-snug">
                        {n.message}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {n.read_notification ? (
                            <ButtonEyeRead onClick={() => toggleReadStatus(n.id)} hoverLabel="Marquer comme non lu" />
                          ) : (
                            <ButtonEyeNoRead onClick={() => toggleReadStatus(n.id)} hoverLabel="Marquer comme lu" />
                          )}
                          <ButtonDelete onClick={() => handleDeleteNotification(n.id)} hoverLabel="Supprimer" />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile */}
          <div className="block sm:hidden">
            {notifications.map((n) => (
              <NotificationCard
                key={n.id}
                notification={n}
                toggleReadStatus={toggleReadStatus}
                handleDeleteNotification={handleDeleteNotification}
              />
            ))}
          </div>
        </>
      ) : (
        <p className="text-center text-slate-600 dark:text-slate-300 text-sm">
          Pas encore de notifications.
        </p>
      )}
    </div>
  );
}
